import React, { useEffect, useState } from 'react';
import { HostDirectoryService, PeerDescriptor, BroadcastDescriptor, ChannelDescriptor } from 'mesh-microkernel';

interface DirectoryDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  directory: HostDirectoryService;
  onSelectPeer?: (nodeId: string) => void;
  onSelectBroadcast?: (broadcast: BroadcastDescriptor) => void;
}

export const DirectoryDrawer: React.FC<DirectoryDrawerProps> = ({
  isOpen,
  onClose,
  directory,
  onSelectPeer,
  onSelectBroadcast,
}) => {
  const [peers, setPeers] = useState<PeerDescriptor[]>([]);
  const [broadcasts, setBroadcasts] = useState<BroadcastDescriptor[]>([]);
  const [channels, setChannels] = useState<ChannelDescriptor[]>([]);

  const refreshDirectory = async () => {
    let pList = await directory.listPeers();
    let bList = await directory.listBroadcasts();
    let cList = await directory.listChannels();

    // Removed mock broadcasts to show actual live mesh state

    setPeers(pList);
    setBroadcasts(bList);
    setChannels(cList);
  };

  useEffect(() => {
    if (!isOpen) return;
    refreshDirectory();

    const unsub = directory.onDirectoryChange(() => {
      refreshDirectory();
    });

    return () => unsub();
  }, [isOpen, directory]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 'var(--app-header-height, 48px)',
        right: 0,
        bottom: 'var(--app-footer-height, 40px)',
        width: '320px',
        backgroundColor: '#1e293b',
        borderLeft: '1px solid #334155',
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '-4px 0 16px rgba(0,0,0,0.4)',
      }}
    >
      <div
        style={{
          padding: '12px 16px',
          borderBottom: '1px solid #334155',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#f8fafc' }}>
          🌐 Host Network Directory
        </h3>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            color: '#94a3b8',
            cursor: 'pointer',
            fontSize: '16px',
          }}
        >
          ✕
        </button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* Peers Section */}
        <div>
          <h4 style={{ fontSize: '12px', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '8px' }}>
            Active Peers ({peers.length})
          </h4>
          {peers.length === 0 ? (
            <div style={{ fontSize: '12px', color: '#64748b' }}>No remote peers discovered yet</div>
          ) : (
            peers.map((peer) => (
              <div
                key={peer.nodeId}
                onClick={() => onSelectPeer && onSelectPeer(peer.nodeId)}
                style={{
                  padding: '8px 10px',
                  backgroundColor: '#0f172a',
                  borderRadius: '6px',
                  marginBottom: '6px',
                  cursor: onSelectPeer ? 'pointer' : 'default',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 500, color: '#f1f5f9' }}>{peer.displayName}</div>
                  <div style={{ fontSize: '11px', color: '#64748b' }}>hns://{peer.nodeId}.mesh</div>
                </div>
                <span
                  style={{
                    fontSize: '10px',
                    padding: '2px 6px',
                    borderRadius: '10px',
                    backgroundColor: peer.status === 'online' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(245, 158, 11, 0.2)',
                    color: peer.status === 'online' ? '#34d399' : '#fbbf24',
                  }}
                >
                  {peer.status}
                </span>
              </div>
            ))
          )}
        </div>

        {/* Broadcasts Section */}
        <div>
          <h4 style={{ fontSize: '12px', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '8px' }}>
            Active Broadcasts ({broadcasts.length})
          </h4>
          {broadcasts.length === 0 ? (
            <div style={{ fontSize: '12px', color: '#64748b' }}>No active broadcasts</div>
          ) : (
            Object.entries(
              broadcasts.reduce((acc, b) => {
                const match = b.topic?.match(/\(#([^)]+)\)/);
                const room = match ? match[1] : 'unknown';
                if (!acc[room]) acc[room] = [];
                acc[room].push(b);
                return acc;
              }, {} as Record<string, typeof broadcasts>)
            ).map(([room, roomBroadcasts]) => (
              <div key={room} style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '4px' }}>
                  #{room}
                </div>
                {roomBroadcasts.map((b) => (
                  <div
                    key={b.uri}
                    className="list-item has-tooltip"
                    data-tooltip={`URI: ${b.uri}`}
                    onClick={() => onSelectBroadcast?.(b)}
                    style={{
                      padding: '8px 10px',
                      backgroundColor: '#0f172a',
                      borderRadius: '6px',
                      marginBottom: '6px',
                      cursor: onSelectBroadcast && (b.pluginId === 'video' || b.pluginId === 'screencast') ? 'pointer' : 'default'
                    }}
                  >
                    <div style={{ fontSize: '13px', fontWeight: 500, color: '#f1f5f9' }}>
                      {b.pluginId === 'video' ? '📹 ' : b.pluginId === 'screencast' ? '💻 ' : ''}{b.topic || b.pluginId}
                    </div>
                    <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>{b.uri}</div>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
